import React, { useState } from "react";
import { EquipoGestion } from "../../../services/v2/prospects.service";

/** Primer nombre de la dueña ("Fanny Castellón" → "Fanny"). */
export const primerNombre = (s: string | null | undefined): string => String(s || "").trim().split(/\s+/)[0] || "";

/** Primer nombre y primer apellido, sin la sigla del plantel ("Estefania Gutierrez Salazar TRC" → "Estefania Gutierrez"). */
export const nombreDuena = (s: string | null | undefined): string => {
	const w = String(s || "").trim().replace(/\s+(TRC|MTY|SPS|TGU)$/i, "").split(/\s+/).filter(Boolean);
	return w.length <= 2 ? w.join(" ") : w[0] + " " + w[w.length >= 4 ? 2 : w.length - 1];
};

/** Cómo se nombra cada área en "Equipo del cliente" ("Antia González (Kits)"). */
export const AREA: { [k: string]: string } = { Venta: "Ventas", Kit: "Kits", Promo: "Promos", "Académico": "Académico" };

/**
 * Equipo del cliente bajo su nombre (CRM y WappChat): todas sus dueñas, una por área (Esteban, 2026-10-13: "María González
 * (Ventas), Antia González (Kits), Martha González (Promos), como múltiples dueñas"). Primero la de la venta, luego cada
 * gestión abierta con cómo va. Lo malo en oro; nada en verde (verde solo Matriculado); lo mío lleno.
 */
const EquipoChips: React.FC<{ equipo?: EquipoGestion[] | null; venta?: string | null; ventaMia?: boolean; compacto?: boolean; className?: string }> = ({ equipo, venta, ventaMia, compacto, className }) => {
	// WappChat (encabezado angosto): primer nombre y área, sin el estado; el detalle va en el globito.
	const nom = compacto ? primerNombre : nombreDuena;
	const abiertas = (equipo || []).filter((g) => !g.cerrada);
	if (!abiertas.length && !venta) return null;
	return (
		<span className={"ec-chips" + (className ? " " + className : "")} onClick={(e) => e.stopPropagation()}>
			{venta ? (
				<span className={"ec-chip" + (ventaMia ? " mia" : "")} title={"Ventas: " + venta}>
					{nom(venta)} <span className="ec-area">(Ventas)</span>
				</span>
			) : null}
			{abiertas.map((g) => (
				<span key={g.id} className={"ec-chip" + (g.malo ? " malo" : "") + (g.mia ? " mia" : "")}
					title={g.objetivo + ": " + g.estadoNombre + (g.owner ? " · " + g.owner : "") + (g.paso ? " — " + g.paso : "")}>
					{g.owner ? nom(g.owner) : "Sin dueña"} <span className="ec-area">({AREA[g.objetivo] || g.objetivo}{compacto ? "" : " · " + g.estadoNombre})</span>
				</span>
			))}
		</span>
	);
};

/**
 * Equipo del cliente en el encabezado de WappChat, en UNA línea (Esteban, 2026-10-15: las pastillas se repetían con el texto
 * de origen y en PC quedaba apretado). PC: "Equipo: Krissy · Ventas  |  Martha · Promos", la mía en naranja, lo malo en oro,
 * con … si no cabe (al pasar el mouse se leen todas). Teléfono: solo la primera y "+2"; al tocarlo se ven todas (la lista va
 * fija en pantalla porque el encabezado recorta lo que se sale).
 * Solo pinta lo que ya trajo la ficha: no pide nada al servidor.
 */
export const EquipoLinea: React.FC<{ equipo?: EquipoGestion[] | null; venta?: string | null; ventaMia?: boolean }> = ({ equipo, venta, ventaMia }) => {
	const [abierto, setAbierto] = useState<{ top: number; left: number } | null>(null);
	const items: { k: string; nombre: string; completo: string; area: string; mia: boolean; malo: boolean; detalle: string }[] = [];
	if (venta) items.push({ k: "v", nombre: primerNombre(venta), completo: nombreDuena(venta), area: "Ventas", mia: !!ventaMia, malo: false, detalle: "Ventas" });
	(equipo || []).filter((g) => !g.cerrada).forEach((g) => items.push({
		k: "g" + g.id, nombre: g.owner ? primerNombre(g.owner) : "Sin dueña", completo: g.owner ? nombreDuena(g.owner) : "Sin dueña",
		area: AREA[g.objetivo] || g.objetivo, mia: !!g.mia, malo: !!g.malo, detalle: g.objetivo + ": " + g.estadoNombre + (g.paso ? " — " + g.paso : ""),
	}));
	if (!items.length) return null;
	const todo = items.map((i) => i.completo + " (" + i.area + ")").join(", ");
	return (
		<span className="ec-linea" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
			<span className="ec-linea-txt" title={todo}>
				<span className="ec-pre">Equipo: </span>
				{items.map((i, n) => (
					<span key={i.k} className={"ec-item" + (i.mia ? " mia" : "") + (i.malo ? " malo" : "") + (n ? " otra" : "")}>{i.nombre}<span className="ec-area"> · {i.area}</span></span>
				))}
			</span>
			{items.length > 1 ? (
				<button type="button" className="ec-mas" aria-expanded={!!abierto} aria-label={"Ver las " + items.length + " dueñas"}
					onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setAbierto(abierto ? null : { top: r.bottom + 4, left: Math.max(8, Math.min(r.left - 40, window.innerWidth - 248)) }); }}>+{items.length - 1}</button>
			) : null}
			{abierto ? <span className="wl-velo" onClick={() => setAbierto(null)} aria-hidden="true" /> : null}
			{abierto ? (
				<span className="ec-pop" role="dialog" aria-label="Equipo del cliente" style={{ top: abierto.top, left: abierto.left }}>
					{items.map((i) => (
						<span key={i.k} className={"ec-pop-fila" + (i.mia ? " mia" : "") + (i.malo ? " malo" : "")}>
							<b>{i.completo}</b><span>{i.detalle}</span>
						</span>
					))}
				</span>
			) : null}
		</span>
	);
};

export default EquipoChips;
